import amplitude from 'amplitude-js';
import { Location } from 'history';

import analyticsConfig from '../config/analytics';
import { UserState } from '../features/user/slice';

export type AnalyticsEvent = {
    name: string;
    attributes: Location | { [key: string]: string | boolean | number | Location | null | undefined | unknown };
};

type AnalyticsEventAttributes = { [key: string]: unknown };

export const obfuscatePath = (path: string) => {
    const guidRegexp = new RegExp(/[0-9A-Fa-f]{8}-?([0-9A-Fa-f]{4}-?){3}[0-9A-Fa-f]{12}/);
    let newPath = path.replace(guidRegexp, '*');

    const referralsRegexp = new RegExp(/\breferrals\b\/[0-9]+/);
    newPath = newPath.replace(referralsRegexp, 'referrals/*');

    const reportsRegexp = new RegExp(/\breport\b\/[0-9]+/);
    newPath = newPath.replace(reportsRegexp, 'report/*');

    const memberRegexp = new RegExp(/\bmember\b\/[0-9]+/);
    newPath = newPath.replace(memberRegexp, 'member/*');

    return newPath;
};

// Record events for Amplitude. The AWS Pinpoint leg was removed in RUN-812.
/**
 * @deprecated use recordEventWithPath instead
 */
export const recordEvent = async (event: AnalyticsEvent) => {
    try {
        amplitude.getInstance().logEvent(event.name, event.attributes);
    } catch (error) {
        console.log('problem recording event');
        console.error(error);
    }
};

export const recordEventWithPath = async (name: string, path: string, attributes: AnalyticsEventAttributes) => {
    try {
        const event = {
            name: name,
            attributes: {
                ...attributes,
                // Last, not first: several call sites pass their own raw `path: location.pathname`
                // in attributes, which overwrote the obfuscated value when this was spread after.
                path: obfuscatePath(path),
            },
        };
        amplitude.getInstance().logEvent(event.name, event.attributes);
    } catch (error) {
        console.error(error);
    }
};

export const registerUserChange = (user: UserState, didCompleteBasicProfile: string, jobTitle: string) => {
    // Set the Amplitude user id
    amplitude.getInstance().setUserId(user.externalId);
    amplitude.getInstance().setUserProperties({
        partnerOrgFullId: user.ownOrgUnit.fullId,
        'Completed Basic Profile': didCompleteBasicProfile,
        'Job Title': jobTitle,
    });

    // update qualaroo identifier
    setTimeout(() => {
        if (window._kiq) {
            // push disableAuto just to be uber sure
            // qualaroo sometimes seems to either ignore it or not receive it
            // window._kiq.push(['disableAuto']);
            window._kiq.push(['identify', user.externalId]);
            window._kiq.push(['set', { partnerOrgFullId: user.ownOrgUnit.fullId }]);
        }
    }, 3000);
};

// Set up Amplitude for product analytics
export const setupAnalytics = () => {
    amplitude.getInstance().init(analyticsConfig.amplitude.apiKey, undefined, {
        apiEndpoint: analyticsConfig.amplitude.apiEndpoint,
        includeUtm: true,
        includeReferrer: true,
        includeGclid: true,
    });
};

export const setupQualaroo = () => {
    /**
     *   Qualaroo - NPS Survey tool
     *   https://qualaroo.com/
     *
     */

    if (!window._kiq) {
        const qualarooScript = document.createElement('script');
        if (window.REACT_APP_NODE_ENV === 'production') {
            qualarooScript.src = 'https://cl.qualaroo.com/ki.js/83291/jhJ.js';
        } else {
            qualarooScript.src = 'https://cl.qualaroo.com/ki.js/83291/ji1.js';
        }

        qualarooScript.async = true;
        qualarooScript.type = 'text/javascript';

        document.body.appendChild(qualarooScript);
    }
};
